import { useMemo, useState, type ReactNode } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type {
  DeclineReasonCode,
  OrderPaymentProofUrlItem,
  OrderStatus,
  PaymentMethod,
  SellerOrderDetail,
} from "@/lib/api/types";
import { formatCurrency } from "@/lib/formatting/currency";
import { getPaymentMethodLabel } from "@/modules/stores/services/payment-method-label";
import { Alert, Banner, TableEmpty, useToast,
  Button,
  DataTable,
  Field,
  Input,
  ItemCell,
  KeyValues,
  Modal,
  Select,
  StatusDot,
  Stepper,
  Timeline,
  type Column,
  type TimelineItem,
} from "@/shared/rd";
import { Meta, SectionCard } from "@/shared/stores/components/rd-local";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import {
  ORDER_EDITABLE_STATUS_VALUES,
  getOrderStatusLabel,
  isEtaRelevantStatus,
  orderStatusTone,
} from "@/modules/stores/services/order-status";
import { PaymentReviewDialog } from "./PaymentReviewDialog";
import { OnlinePaymentPanel } from "./OnlinePaymentPanel";
import { OrderAddressBlock } from "./OrderAddressBlock";

type Props = {
  locale: string;
  storeId: string;
  order: SellerOrderDetail;
  proofState: {
    items: OrderPaymentProofUrlItem[];
    isLoading: boolean;
    isError: boolean;
  };
  onUpdateStatus: (payload: {
    status: OrderStatus;
    statusEtaDays: number | null;
    declineReasonCode?: DeclineReasonCode;
    declineReasonNote?: string;
  }) => Promise<SellerOrderDetail>;
};

const declineOptions: Array<{ value: DeclineReasonCode; labelKey: string }> = [
  {
    value: "amount_not_received",
    labelKey: keys.orders.declineAmountNotReceived,
  },
  { value: "amount_not_match", labelKey: keys.orders.declineAmountNotMatch },
];

/** The fixed stages an accepted order moves through. */
const FULFILMENT_STAGES: OrderStatus[] = ["confirmed", "preparing", "shipped", "delivered"];

type OrderItem = SellerOrderDetail["items"][number];

function normalizeEtaValue(value: number | null): string {
  return value === null ? "" : String(value);
}

function mapStatusUpdateError(
  error: ApiJsonClientError,
  nextStatus: OrderStatus,
  t: ReturnType<typeof useTranslations>,
): string {
  // Stale conflict — the backend now emits code 'STALE_STATUS'; fall back to
  // the 409 status for responses that carry no machine code.
  if (error.code === "STALE_STATUS" || error.status === 409) {
    return t(keys.orders.paymentReviewConflictNotice);
  }

  // Validation errors arrive as class-validator 400s: a message (array) in the
  // payload with no machine code. Surface the server's message when present so
  // the reviewer sees the actionable reason; otherwise fall back to ETA copy.
  // Client-side pre-validation already blocks missing/negative/NaN ETA.
  if (error.status === 400) {
    const serverMessage = error.message.trim();
    if (serverMessage.length > 0 && serverMessage !== "Request failed") {
      return serverMessage;
    }
    if (nextStatus === "shipped" || nextStatus === "preparing") {
      return t(keys.orders.etaRequiredForStatusUpdate);
    }
    return t(keys.orders.etaMustBeValidNumber);
  }

  return t(keys.errors.generic);
}

export function OrderDetails({
  locale,
  storeId,
  order,
  proofState,
  onUpdateStatus,
}: Props) {
  const t = useTranslations();
  const [orderState, setOrderState] = useState(order);

  const [draftStatus, setDraftStatus] = useState<OrderStatus>(
    orderState.status,
  );
  const [draftEta, setDraftEta] = useState<string>(
    normalizeEtaValue(orderState.statusEtaDays),
  );
  const [draftDeclineCode, setDraftDeclineCode] = useState<
    DeclineReasonCode | ""
  >(orderState.declineReasonCode ?? "");
  const [draftDeclineNote, setDraftDeclineNote] = useState(
    orderState.declineReasonNote ?? "",
  );

  const [savedStatus, setSavedStatus] = useState<OrderStatus>(
    orderState.status,
  );
  const [savedEta, setSavedEta] = useState<number | null>(
    orderState.statusEtaDays,
  );
  const [savedDeclineCode, setSavedDeclineCode] =
    useState<DeclineReasonCode | null>(orderState.declineReasonCode);
  const [savedDeclineNote, setSavedDeclineNote] = useState(
    orderState.declineReasonNote ?? "",
  );
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();
  /** The server refused the change: a banner at the top of the card. */
  const [saveError, setSaveError] = useState<string | null>(null);
  /** Said under the field that needs fixing. */
  const [etaError, setEtaError] = useState<string | null>(null);
  const [declineError, setDeclineError] = useState<string | null>(null);
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const proofItems = proofState.items;
  const isProofLoading = proofState.isLoading;
  const proofError = proofState.isError;

  // Re-sync local draft/saved state whenever a fresh order comes in from the
  // server (adjust-state-during-render pattern instead of an effect).
  const [prevOrder, setPrevOrder] = useState(order);
  if (order !== prevOrder) {
    setPrevOrder(order);
    setOrderState(order);
    setDraftStatus(order.status);
    setDraftEta(normalizeEtaValue(order.statusEtaDays));
    setDraftDeclineCode(order.declineReasonCode ?? "");
    setDraftDeclineNote(order.declineReasonNote ?? "");
    setSavedStatus(order.status);
    setSavedEta(order.statusEtaDays);
    setSavedDeclineCode(order.declineReasonCode);
    setSavedDeclineNote(order.declineReasonNote ?? "");
  }

  const statusOptions = useMemo(
    () =>
      ORDER_EDITABLE_STATUS_VALUES.map((status) => ({
        value: status,
        label: getOrderStatusLabel(t, status),
      })),
    [t],
  );

  const hasChanges =
    draftStatus !== savedStatus ||
    draftEta !== normalizeEtaValue(savedEta) ||
    draftDeclineCode !== (savedDeclineCode ?? "") ||
    draftDeclineNote.trim() !== savedDeclineNote.trim();
  const canReviewPayment =
    orderState.status === "pending_payment_verification" &&
    storeId.length > 0;

  const submitStatusUpdate = async (allowDecline: boolean) => {
    const etaInput = draftEta;
    const hasEtaInput = etaInput.trim() !== "";
    const etaDays = hasEtaInput ? Number(etaInput) : null;

    if (
      isEtaRelevantStatus(draftStatus) &&
      draftStatus !== "confirmed" &&
      !hasEtaInput
    ) {
      setEtaError(t(keys.orders.etaRequiredForStatusUpdate));
      return;
    }

    if (etaDays !== null && (!Number.isFinite(etaDays) || etaDays < 0)) {
      setEtaError(t(keys.orders.etaMustBeValidNumber));
      return;
    }
    setEtaError(null);

    if (draftStatus === "declined" && !draftDeclineCode) {
      setDeclineError(t(keys.orders.declineReasonPlaceholder));
      setShowDeclineConfirm(false);
      return;
    }
    setDeclineError(null);

    if (draftStatus === "declined" && !allowDecline) {
      setShowDeclineConfirm(true);
      return;
    }

    setSaveError(null);
    setIsSaving(true);

    const payload: {
      status: OrderStatus;
      statusEtaDays: number | null;
      declineReasonCode?: DeclineReasonCode;
      declineReasonNote?: string;
    } = {
      status: draftStatus,
      statusEtaDays: isEtaRelevantStatus(draftStatus) ? etaDays : null,
    };

    if (draftStatus === "declined") {
      payload.declineReasonCode = draftDeclineCode as DeclineReasonCode;
      payload.declineReasonNote = draftDeclineNote || undefined;
    }

    try {
      const updatedOrder = await onUpdateStatus(payload);

      setOrderState(updatedOrder);
      setSavedStatus(draftStatus);
      setSavedEta(isEtaRelevantStatus(draftStatus) ? etaDays : null);
      setSavedDeclineCode(
        draftStatus === "declined"
          ? (draftDeclineCode as DeclineReasonCode)
          : null,
      );
      setSavedDeclineNote(draftStatus === "declined" ? draftDeclineNote : "");
      setShowDeclineConfirm(false);
      toast.show({
        message: t("orders.statusSavedToast", {
          code: updatedOrder.orderCode,
          status: getOrderStatusLabel(t, draftStatus),
        }),
      });
    } catch (caughtError) {
      if (caughtError instanceof ApiJsonClientError) {
        setSaveError(mapStatusUpdateError(caughtError, draftStatus, t));
      } else {
        setSaveError(t(keys.errors.generic));
      }
      setShowDeclineConfirm(false);
    } finally {
      setIsSaving(false);
    }
  };

  const formatWhen = (iso: string) => new Date(iso).toLocaleString(locale);
  const itemName = (item: OrderItem) =>
    locale === "en"
      ? (item.resolvedTitleEn ?? item.productNameEn ?? item.resolvedTitleAr ?? item.productNameAr)
      : (item.resolvedTitleAr ?? item.productNameAr);
  const itemOptions = (item: OrderItem) =>
    item.selectedOptions
      .map((option) => (locale === "en" ? (option.value.en ?? option.value.ar) : option.value.ar))
      .join(" · ");

  const itemColumns: Array<Column<{ key: string; item: OrderItem }>> = [
    {
      key: "item",
      label: t(keys.orders.itemsTitle),
      width: 3,
      render: ({ item }) => (
        <ItemCell badge="package" name={itemName(item)} meta={item.selectedOptions.length > 0 ? itemOptions(item) : undefined} />
      ),
    },
    {
      key: "line",
      label: t("orders.itemQtyPriceColumn"),
      width: 1.4,
      align: "end",
      numeric: true,
      render: ({ item }) => (
        <span>
          {item.quantity} × {formatCurrency(item.unitPrice, locale)}
        </span>
      ),
    },
  ];

  const history = orderState.statusHistory ?? [];
  const timelineItems: TimelineItem[] = history.map((entry) => {
    const notes = [
      entry.statusEtaDays !== null ? `${t(keys.orders.estimatedDeliveryLabel)}: ${entry.statusEtaDays}` : null,
      entry.note || null,
      entry.declineReasonNote || null,
    ].filter(Boolean);
    return {
      id: entry.id,
      time: formatWhen(entry.changedAt),
      tone: orderStatusTone(entry.newStatus) === "red" ? "bad" : orderStatusTone(entry.newStatus) === "green" ? "good" : "ink",
      filled: true,
      label: (
        <span>
          {getOrderStatusLabel(t, entry.oldStatus)}{" "}
          <span aria-hidden="true" style={{ color: "var(--rd-text-meta)" }}>
            →
          </span>{" "}
          {getOrderStatusLabel(t, entry.newStatus)}
        </span>
      ),
      note: notes.length > 0 ? notes.join(" · ") : undefined,
    };
  });
  const stageIndex = FULFILMENT_STAGES.indexOf(orderState.status);
  const stageDate = (stage: OrderStatus) => {
    const entry = [...history].reverse().find((row) => row.newStatus === stage);
    return entry ? new Date(entry.changedAt).toLocaleDateString(locale) : undefined;
  };

  const summaryRows: Array<{ label: ReactNode; value: ReactNode }> = [
    {
      label: t(keys.orders.orderCodeLabel),
      value: (
        <span dir="ltr" style={{ fontWeight: 600 }}>
          {orderState.orderCode}
        </span>
      ),
    },
    {
      label: t(keys.orders.paymentMethodLabel),
      value: getPaymentMethodLabel(orderState.paymentMethod as PaymentMethod, t),
    },
    {
      label: t(keys.orders.totalLabel),
      value: (
        <span className="rd-num" style={{ fontWeight: 600 }}>
          {formatCurrency(orderState.totalAmount, locale)}
        </span>
      ),
    },
  ];
  if (orderState.couponDiscountAmount > 0) {
    summaryRows.push({
      label: t(keys.orders.discountLabel),
      value: (
        <span className="rd-num" style={{ color: "var(--rd-green)" }}>
          −{formatCurrency(orderState.couponDiscountAmount, locale)}
        </span>
      ),
    });
  }
  if (orderState.couponCode) {
    summaryRows.push({
      label: t(keys.orders.couponLabel),
      value: (
        <span dir="ltr" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontWeight: 600 }}>
          {orderState.couponCode}
        </span>
      ),
    });
  }

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))", gap: 16, alignItems: "start" }}>
        <SectionCard
          badge="bill"
          title={t(keys.orders.orderDetailsTitle)}
          aside={
            <StatusDot tone={orderStatusTone(orderState.status)}>
              {getOrderStatusLabel(t, orderState.status)}
            </StatusDot>
          }
        >
          <KeyValues rows={summaryRows} />
          {canReviewPayment ? (
            <div>
              <Button variant="primary" size="sm" onClick={() => setIsReviewDialogOpen(true)}>
                {t(keys.orders.reviewAction)}
              </Button>
            </div>
          ) : null}
        </SectionCard>

        <SectionCard badge="team" title={t(keys.orders.buyerInfoTitle)}>
          {/* TODO(privacy-mode): when privacy mode is enabled, mask phone/address values in details too. */}
          <KeyValues
            rows={[
              {
                label: t(keys.orders.buyerNameLabel),
                value: (
                  <span dir="auto" style={{ fontWeight: 600 }}>
                    {orderState.customer.name}
                  </span>
                ),
              },
              {
                label: t(keys.orders.buyerPhoneLabel),
                value: (
                  <bdi dir="ltr" className="rd-num">
                    {orderState.customer.phoneE164}
                  </bdi>
                ),
              },
              {
                label: t(keys.orders.buyerAddressLabel),
                value: <OrderAddressBlock address={orderState.address} fallback={orderState.buyerAddress} />,
              },
              {
                label: t(keys.orders.buyerNoteLabel),
                value: (
                  <span dir="auto">{orderState.buyerNote?.trim() ? orderState.buyerNote : t(keys.common.empty)}</span>
                ),
              },
            ]}
          />
        </SectionCard>
      </div>

      <OnlinePaymentPanel storeId={storeId} orderId={orderState.id} locale={locale} />

      <SectionCard badge="todo" title={t(keys.orders.statusManagementTitle)}>
        {saveError ? (
          <Banner
            tone="error"
            title={t("orders.statusFailedTitle")}
            actions={
              <Button
                size="sm"
                loading={isSaving}
                loadingLabel={t(keys.orders.savingAction)}
                onClick={() => void submitStatusUpdate(draftStatus === "declined")}
              >
                {t(keys.shell.retry)}
              </Button>
            }
          >
            {saveError} {t("admin.nothingChanged")}
          </Banner>
        ) : null}
        <div className="rd-fields-inline">
          <Field label={t(keys.orders.statusLabel)} htmlFor={`order-status-${orderState.id}`}>
            <Select
              id={`order-status-${orderState.id}`}
              options={statusOptions}
              value={draftStatus}
              onChange={(event) => setDraftStatus(event.target.value as OrderStatus)}
              style={{ minWidth: 220 }}
            />
          </Field>

          {isEtaRelevantStatus(draftStatus) ? (
            <Field
              label={t(keys.orders.estimatedDeliveryLabel)}
              help={t(keys.orders.estimatedDeliveryHelper)}
              htmlFor={`order-eta-${orderState.id}`}
              error={etaError ?? undefined}
            >
              <Input
                id={`order-eta-${orderState.id}`}
                type="number"
                name="estimatedDeliveryDays"
                min={0}
                value={draftEta}
                invalid={Boolean(etaError)}
                onChange={(event) => {
                  setDraftEta(event.target.value);
                  setEtaError(null);
                }}
                style={{ width: 160 }}
              />
            </Field>
          ) : null}
        </div>

        {draftStatus === "declined" ? (
          <div className="rd-fields-inline">
            <Field
              label={t(keys.orders.declineReasonLabel)}
              htmlFor={`order-decline-${orderState.id}`}
              error={declineError ?? undefined}
            >
              <Select
                id={`order-decline-${orderState.id}`}
                placeholder={t(keys.orders.declineReasonPlaceholder)}
                options={declineOptions.map((option) => ({ value: option.value, label: t(option.labelKey) }))}
                value={draftDeclineCode}
                onChange={(event) => {
                  setDraftDeclineCode(event.target.value as DeclineReasonCode | "");
                  setDeclineError(null);
                }}
                style={{ minWidth: 220 }}
              />
            </Field>
            <Field label={t(keys.orders.declineNoteLabel)} htmlFor={`order-decline-note-${orderState.id}`}>
              <Input
                id={`order-decline-note-${orderState.id}`}
                type="text"
                value={draftDeclineNote}
                onChange={(event) => setDraftDeclineNote(event.target.value)}
                style={{ minWidth: 260 }}
              />
            </Field>
          </div>
        ) : null}

        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <Button
            variant="primary"
            disabled={!hasChanges}
            loading={isSaving && !showDeclineConfirm}
            loadingLabel={t(keys.orders.savingAction)}
            onClick={() => void submitStatusUpdate(false)}
          >
            {t(keys.orders.updateStatusAction)}
          </Button>
        </div>
      </SectionCard>

      <DataTable
        label={t(keys.orders.itemsTitle)}
        columns={itemColumns}
        rows={orderState.items.map((item, index) => ({ key: `${item.productVariantId}-${index}`, item }))}
        rowKey={(row) => row.key}
        minWidth={480}
        empty={<TableEmpty title={t("orders.itemsEmptyTitle")} />}
      />

      <section id="status-history" className="rd-card" style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{t(keys.orders.statusHistoryTitle)}</h2>
        {stageIndex >= 0 ? (
          <Stepper
            steps={FULFILMENT_STAGES.map((stage, index) => ({
              label: getOrderStatusLabel(t, stage),
              date: index <= stageIndex ? stageDate(stage) : undefined,
              state: index <= stageIndex ? "done" : "todo",
              current: index === stageIndex,
            }))}
          />
        ) : null}
        {timelineItems.length === 0 ? <Meta>{t(keys.common.empty)}</Meta> : <Timeline items={timelineItems} />}
      </section>

      <SectionCard badge="camera" title={t(keys.orders.proofUploadedLabel)}>
        {isProofLoading ? (
          <Meta>{t(keys.common.loading)}</Meta>
        ) : proofError ? (
          <Alert tone="error">{t(keys.orders.proofLoadError)}</Alert>
        ) : proofItems.length === 0 ? (
          <Meta>{t(keys.common.empty)}</Meta>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 260px), 1fr))", gap: 12 }}>
            {proofItems.map((proof) => (
              <figure key={proof.proofId} style={{ margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                <div
                  style={{
                    position: "relative",
                    height: 280,
                    overflow: "hidden",
                    borderRadius: "var(--rd-radius-card)",
                    background: "var(--rd-surface-sunken)",
                  }}
                >
                  <img
                    src={proof.url}
                    alt={t(keys.orders.proofUploadedLabel)}
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
                  />
                </div>
                <figcaption style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 13, color: "var(--rd-text-secondary)" }}>
                  <span className="rd-num" style={{ fontWeight: 600, color: "var(--rd-text)" }}>
                    {formatCurrency(proof.paidAmount, locale)}
                  </span>
                  <span>{formatWhen(proof.uploadedAt)}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </SectionCard>

      <Modal
        open={showDeclineConfirm}
        onClose={isSaving ? undefined : () => setShowDeclineConfirm(false)}
        title={t(keys.orders.confirmDeclineTitle)}
        actions={
          <>
            <Button variant="ghost" muted disabled={isSaving} onClick={() => setShowDeclineConfirm(false)}>
              {t(keys.common.cancel)}
            </Button>
            <Button
              variant="danger"
              loading={isSaving}
              loadingLabel={t(keys.orders.savingAction)}
              onClick={() => void submitStatusUpdate(true)}
            >
              {t(keys.orders.confirmDeclineAction)}
            </Button>
          </>
        }
      >
        <p className="rd-modal-text" style={{ margin: 0 }}>
          {t(keys.orders.confirmDeclineBody)}
        </p>
      </Modal>

      <PaymentReviewDialog
        locale={locale}
        storeId={storeId}
        orderId={isReviewDialogOpen ? orderState.id : null}
        isOpen={isReviewDialogOpen}
        onClose={() => setIsReviewDialogOpen(false)}
        onSuccess={(updatedOrder) => {
          setOrderState(updatedOrder);
          setDraftStatus(updatedOrder.status);
          setDraftEta(normalizeEtaValue(updatedOrder.statusEtaDays));
          setSavedStatus(updatedOrder.status);
          setSavedEta(updatedOrder.statusEtaDays);
          setSavedDeclineCode(updatedOrder.declineReasonCode);
          setSavedDeclineNote(updatedOrder.declineReasonNote ?? "");
          setIsReviewDialogOpen(false);
        }}
      />
    </>
  );
}
